import type {
  CommonOptionsType,
  PromptMappingTableType,
} from '@/definition/common/data.d';
import type { ReactNode } from 'react';
import type {
  LocationCodingEnum,
  subStatusOptEnum,
  subscribedStatusEnum,
} from '../utils/enum/add-or-edit';

/** Source类型 */
export enum SourceTypeEnum {
  /** 默认值 */
  default = 0,
  /** Upload a file */
  uploadAFile = 1,
  /** Via API */
  viaApi = 2,
  /** Add a subscriber */
  Manual = 3,
  /** LEVEL INFINITE PASS */
  levelInfinitePass = 4,
  /** 预约邮件 */
  newsletterSignUp = 5,
  /** Source */
  SOURCE = 10,
}

/** Add Contacts 上传文件Table */
export enum uploadTableEnum {
  /** View by location */
  ViewByLocation = 1,
  /** View by languages */
  ViewByLanguages = 2,
}

export enum ValueType {
  number = 'number',
}

export enum ContactInputEnum {
  custom = 'custom',
  text = 'text',
  input = 'input',
  textarea = 'textarea',
  select = 'select',
  pagingSelect = 'pagingSelect',
  checkboxGroup = 'checkboxGroup',
  radioGroup = 'radioGroup',
  datePicker = 'datePicker',
  inputNumber = 'inputNumber',
}

// 输入框类型声明
export type ContactInputType =
  | 'custom'
  | 'input'
  | 'textarea'
  | 'select'
  | 'checkboxGroup'
  | 'radioGroup'
  | 'inputNumber'
  | string;

export type PatternArrType = {
  /** 校验规则 */
  checkRule: Function;
  /** 校验不通过，提示信息 */
  msg: string;
};

// 简易表单 - 映射类型声明
export type ContactPromptMappingTableType = {
  // 值类型
  valueType?: string;
  /** 录入内容验证规则 */
  patternArr?: PatternArrType[];
  /** 表单额外说明信息 */
  extra?: ReactNode | string;
} & PromptMappingTableType;

export type sourceTypes =
  | 'uploadFile'
  | 'api'
  | 'subscriber'
  | 'level'
  | 'newsletterSignUp';
// 来源
export type SourceFormOptionTypes = {
  /** 分类 */
  type: sourceTypes;
  /** 标题 */
  title?: string | ReactNode;
  /** 标题 - 旁气泡描述 */
  titleBubbleDes?: string | ReactNode;
  /** 描述 */
  desc?: string | ReactNode;
  /** 禁用标识 */
  disabled?: boolean;
  /** 值 */
  value: SourceTypeEnum;
  /** 图标 */
  icon: string;
  /** 图标选中效果 */
  iconActived: string;
  /** 路由 */
  path?: string;
  /** 是否显示 */
  isShow?: boolean;
  // 是否为img格式
  isImg?: boolean;
};

// 上传文件 - 步骤类型
export type uploadFileStepsTypes =
  | 'uploadFile'
  | 'matchProperties'
  | 'tag'
  | 'review'
  | 'subscribeStatus'
  | 'updateContacts'
  | 'preference';

/**
 * 上传文件 - 标识表头字段来源
 * fileCustomKey: 上传文件中自定义新增的表头
 * existingField：数据库中已存在的表头
 * modalAddField：通过弹窗自定义新增的表头
 */
export type fieldSourceTypes =
  | 'fileCustomKey'
  | 'existingField'
  | 'modalAddField';

// 上传文件
export type DealWithResHeaderResTypes = {
  /** 上传文件 - 标题 */
  fileHeader: string | undefined;
  /** 上传文件 - 类型 */
  fileType: string | undefined;
};

export interface ContactManageDataTypes {
  /** 上传文件 - 文件信息 */
  fileListArr: any[];
  /** 上传文件 - 确认授权 */
  confirmUpload: boolean;
  /** 上传文件 - 标签信息 */
  tags: any[];
  /** 上传文件 - 上传结果 */
  fileList?: any;
  /** 上传文件 - 表头 */
  fileListHeader?: tableHeaderTypes[];
  /** 上传文件 - 订阅状态 */
  subscriptionStatus: number | undefined;
  /** 上传文件 - 否更新已存在的联系人信息和订阅状态 */
  isUpdate?: number;
  /** 上传文件解析后表头及数据的二维数组 */
  fileList?: { contactManageList: any[][] };
  /** 文件编码 */
  fileCoding?: string;
  /** 上传文件 - 状态 - 对系统已存在的联系人的其他字段信息是否更新 */
  updateState: number;
  /** 上传文件 - 状态 - 偏好 */
  preferenceIds: number[];
  /** 上传文件 - Review - 确认导入 */
  confirmImport: number;
  /** 第一步骤中 - 解析上传的文件内地区编码列是否有数据 */
  locationColHasData: boolean;
  /** 地点编码方式 */
  locationCoding: LocationCodingEnum;
  /** 订阅状态 - 子选项 */
  subscriptionTypeSubOptions: number;
}

/** 已开通Whatspp业务上传文件：数据类型 */
export type WhatsAppContactManageDataTypes = {
  /** whatsApp - 订阅状态 */
  wuidSubscribedType: subscribedStatusEnum | undefined;
  /** whatsApp - 订阅状态 - 子选项 */
  wuidSubscriptionTypeSubOptions: subStatusOptEnum;
} & ContactManageDataTypes;

export type tableHeaderTypes = {
  /** 上传文件过程获取的表头名字 */
  fileHeader?: string;
  /** 上传文件过程获取的表头名程列对应的类型 */
  fileType?: string | undefined;
  /** 页面显示的表头文案 */
  text: string;
  /** 页面表头文案key */
  value: string;
  /** 当前表头位置 */
  position: number;
  /** 显示于表头上的信息，下拉框中禁用 */
  disabled: boolean;
  /** 控制信息是否显示于表格上 */
  isShow: boolean;
  /** 标识表头为弹窗新增字段 */
  addByModal?: boolean;
  /** 标识表头字段来源 */
  fieldSource?: fieldSourceTypes;
  /** 允许导入 */
  imported?: boolean;
  /** 固定字段标识 */
  systemField: string;
  /** dropDown类型携带信息 */
  option?: string;
  /** dropDown类型携带信息 - 处理成了数组 */
  dropDownOption?: OptionsTypes[];
  /** dropDown类型携带信息 - 处理成了数组 */
  optionName?: OptionsTypes[];
  /** system:系统字段  extra:自定义字段 */
  classify?: string;
  /** 功能类型类型  1常规类型 2内置类型 */
  functionalType?: number;
};

export interface CommonOptsType extends CommonOptionsType {
  /** 推荐 */
  recom?: boolean;
  /** 描述 */
  desc?: string;
  /** 补充解释 */
  extra?: string | ReactNode;
}

export type subscribersFormItemType = {
  /** 表单item展示类型 */
  inputType: ContactInputType;
  // 值类型
  valueType?: string;
  /** 键名 */
  field: string;
  /** 表单项说明 */
  label: string;
  /** 表单内容不符合规则提示信息 */
  msgKey?: string;
  /** 录入内容验证规则 */
  patternArr?: PatternArrType[];
  /** 自定义类名 */
  className?: string;
  /** 位置 */
  align?: string;
  /** 是否为必填项 */
  required?: boolean;
  // 选项配置
  options?: any[];
  /** 内容props - 例如设置input的maxlength placeholder */
  contentProps?: any;
  // onChange?: (
  //   val: any,
  //   contxt?: { onClearError?: (val: string) => void },
  // ) => void;
  /** 自定义渲染 */
  // onRender?: () => any;
  /** 表单额外说明信息 */
  extra?: ReactNode | string;
  /** 表单值 */
  value: any;
  errorStatus?: any;
  errorMsg?: any;
  /** 字段归属 extra => 自定义字段 */
  filedOwnership?: string;
  /** 字段归属 extra => 自定义字段类型 */
  extraType?: 'int' | 'string' | 'date';
  /** 字段ID */
  id?: string | number;
  // 自定义校验
  validator?: Function;
  // 自定义校验提示
  validatorErrorMsg?: string;
};

/** 订阅状态级联选项类型 */
export type subscriptionOptsListTypes = {
  label: string;
  value: string;
  /** 位置 */
  index?: string;
  children?: subscriptionOptsListTypes[];
};

/** 联系人上传文件 - 邮件域名验证结果 */
export enum EmailDomainCheckEnum {
  /** 验证通过 */
  CHECK_THROUGH = 'checkThrough',
  /** 验证不通过 */
  CHECK_UN_THROUGH = 'checkUnThrough',
  /** 待验证 */
  WEATING_CHECK = 'weatingCheck',
}

/** 联系人上传文件 - 缓存的邮件域名数据类型 */
export type SessionEmailDomainTypes = {
  [key: string]: EmailDomainCheckEnum;
};

interface CsvToTableTypes {
  /** 上传的文件 */
  file: File;
  /** 回调 */
  callBack?: Function;
  /** 文件是csv */
  isCsv?: boolean;
  // callBack: any
  /** 全局数据 */
  data: WhatsAppContactManageDataTypes | ContactManageDataTypes;
  /** 下一步id */
  next: { id: uploadFileStepsTypes; label: string } | undefined;
  /** 解析进度百分比 */
  setPercent: (val: number) => void;
  /** 校验后待提交成功文件 */
  setArrSuccList: (val: any[]) => void;
  /** 是否开始记录解析进度 */
  setStartClock: (val: boolean) => void;
  /** 触发下一步标识 */
  setSubmit: (val: boolean) => void;
  /** 遮罩 */
  setOverlayVisible: (val: boolean) => void;
  /** 重置data数据 */
  set: (val: any) => void;
  /** 设置进度百分比 */
  percentChange: (val: number) => void;
  /** 【上传文件】步骤 - 获取已存在的文件表头信息，为第二部做准备 */
  getCustomFieldFn: (val: any) => void;
  /** 当前激活步骤的 key */
  setCurrent: (val: uploadFileStepsTypes) => void;
  /** 开启弹窗选择地点编码 */
  onOpenLocationCoding: () => void;
}

export const toInspectCol = [];

// 有效表头下标 - 符合传递至cos表头下标 & 表头详细信息
export type effectHeaderType = {
  /** 有效表头下标 - 符合传递至cos表头下标 */
  index: number;
  /** 表头详细信息 */
  headerInfo: tableHeaderTypes;
};

/**
 * 【标签】步骤 - 向下执行到Review - emailDomainChecked：接口验证域名有效性
 * 入参类型
 */
export type apiCheckEmailDomainTypes = {
  /** 所有域名信息 & 待接口校验域名 */
  temporaryStorage: SessionEmailDomainTypes[];
  /** 进度 */
  percentChange: Function;
  /** 终止上传 */
  isCancelUpload?: boolean;
  /** 占比 */
  proportionCheckDomain: number;
  /** 已执行的占比值 */
  basePercentCheckDomain: number;
  /** 本次经过接口校验的域名变量赋值 */
  pushCurrentDomainRes: (val) => void;
};

// 上传文件 - 说明
export enum uploadFileInstEnum {
  COUNTRY_LANGUAGE = '1',
  TAGS_PERFERENCES = '2',
  DATE_TIME = '3',
}
